import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEdit } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import './Profile.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

export default function Profile() {
  const { token } = useAuth();
  const [profile, setProfile] = useState({ name: '', email: '', username: '', phone: '' });
  const [stats, setStats] = useState({ total_orders: 0, completed_orders: 0, total_spent: 0, balance: 0 });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...profile });
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/profile`, { headers })
      .then((r) => r.json())
      .then((d) => {
        if (d.user) {
          setProfile({ name: d.user.name, email: d.user.email, username: d.user.username, phone: d.user.phone || '' });
          setForm({ name: d.user.name, email: d.user.email, username: d.user.username, phone: d.user.phone || '' });
        }
        if (d.stats) setStats(d.stats);
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: form.name, username: form.username, phone: form.phone }),
      });
      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();
      setProfile({ name: data.name, email: data.email, username: data.username, phone: data.phone || '' });
      setEditing(false);
    } catch { /* ignore */ } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({ ...profile });
    setEditing(false);
  };

  return (
    <div className="profile-page">
      <div className="profile-grid">
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar">
              <FontAwesomeIcon icon={faUser} />
            </div>
            <div className="profile-header-info">
              <h2 className="profile-name">{profile.name}</h2>
              <span className="profile-username">@{profile.username}</span>
            </div>
            {!editing && (
              <button className="edit-btn" onClick={() => setEditing(true)}>
                <FontAwesomeIcon icon={faEdit} />
                <span>Edit</span>
              </button>
            )}
          </div>

          <div className="profile-details">
            {editing ? (
              <>
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={form.email}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="form-actions">
                  <button className="save-btn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
                </div>
              </>
            ) : (
              <div className="detail-list">
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{profile.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Username</span>
                  <span className="detail-value">@{profile.username}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{profile.phone || '—'}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="stats-section">
          <h2 className="section-title">Dashboard</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-value">{stats.total_orders}</span>
              <span className="stat-label">Total Orders</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.completed_orders}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">TSHS {Number(stats.total_spent || 0).toLocaleString()}</span>
              <span className="stat-label">Total Spent</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">TSHS {Number(stats.balance || 0).toLocaleString()}</span>
              <span className="stat-label">Balance</span>
            </div>
          </div>

          <h2 className="section-title" style={{ marginTop: 32 }}>Recent Activity</h2>
          <div className="activity-list">
            <div className="activity-item">
              <span className="activity-dot info"></span>
              <div className="activity-info">
                <span className="activity-text">Profile data loaded from server</span>
                <span className="activity-time">—</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
